import { API_URL, SPJWT } from "@/configs";
import { getCookie } from "@/utils/session";

import { backendPublicPath, getServiceUrlOrigin, joinServiceUrl } from "./servicePaths";

type RealtimeTokenResponse = {
  token: string;
  expiresAt?: number;
};

let cachedToken: { token: string; expiresAt: number } | null = null;
let inFlightTokenPromise: Promise<string | null> | null = null;

function normalizeExpiryMs(expiresAt?: number): number {
  const rawExpiry = Number(expiresAt);
  if (!Number.isFinite(rawExpiry) || rawExpiry <= 0) {
    return Date.now() + 300_000;
  }

  return rawExpiry > 1_000_000_000_000 ? rawExpiry : rawExpiry * 1000;
}

export function clearRealtimeTokenCache() {
  cachedToken = null;
  inFlightTokenPromise = null;
}

export async function fetchRealtimeToken(forceRefresh = false): Promise<string | null> {
  if (!forceRefresh && cachedToken && cachedToken.expiresAt - Date.now() > 30_000) {
    return cachedToken.token;
  }

  if (inFlightTokenPromise) {
    return inFlightTokenPromise;
  }

  const accessToken = getCookie(SPJWT);
  if (!accessToken) {
    return null;
  }

  const realtimeTokenUrl = joinServiceUrl(
    getServiceUrlOrigin(API_URL) || API_URL,
    backendPublicPath("/stokens/realtime-token/")
  );

  inFlightTokenPromise = fetch(realtimeTokenUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const data = (await response.json()) as RealtimeTokenResponse;
      if (!data?.token) return null;
      cachedToken = {
        token: data.token,
        expiresAt: normalizeExpiryMs(data.expiresAt)
      };
      return data.token;
    })
    .catch(() => null)
    .finally(() => {
      inFlightTokenPromise = null;
    });

  return inFlightTokenPromise;
}
